import React, { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Theme,
} from "@mui/material";
import CustomInputButton from "../../components/Input/customInputButton";
import DamsTable from "../../components/Table/CollapsibleTable";
import { TRowItem } from "../../components/Table/TableRow";
import { usePagination } from "../../hooks/usePagination";

import { ContainedButton, TextButton } from "../../components/Button/AppButton";
import { IPagination, Roles } from "../../config/types";
import { rolesApi } from "../../services/roles";
import { alertDialogStore } from "../../store/dialogs";

interface InputProps {
  placeholder: string;
  label: string;
  handleChange: (items: TRowItem[]) => void;
  value?: TRowItem[];
  size?: "full" | "short" | "long";
  error?: boolean;
  errorMessage?: string;
}

export const PermissionSelectInput = ({
  placeholder,
  handleChange,
  label,
  error,
  value,
  size = "full",
  errorMessage = "required",
}: InputProps) => {
  const [dataset, setDataset] = useState<TRowItem[]>([]);
  const [selectName, setSelectName] = useState("");
  const [loading, setLoading] = useState(true);

  const [selectedRowItems, setSelectedRowItems] = useState<TRowItem[]>([]);
  const [allPermissions, setAllPermissions] = useState<TRowItem[]>([]);

  const {
    pagination,
    paginateToNextPage,
    paginateToPreviousPage,
    paginateToPageNumber,
    updatePaginationInitState,
  } = usePagination<any>(rolesApi.getPermission);

  const populateDataSet = (data: any[], pagination?: IPagination) => {
    setDataset(data);
    if (pagination) {
      updatePaginationInitState({
        ...pagination,
        total_pages: pagination.total_pages,
      });
    }
  };

  const fetchAllPermissions = async () => {
    try {
      const res = await rolesApi.getPermission({
        is_deleted: false,
        page_size: 1000,
      });
      if (res.data) {
        setAllPermissions(res.data);
      }
    } catch (error) {
      alertDialogStore.open({
        title: "Failed to Load Permissions",
        subtitle: "Unable to fetch permissions. Please try again.",
        isSuccess: false,
      });
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const res = await rolesApi.getPermission({
        is_deleted: false,
        page: 1,
        page_size: 30,
      });

      if (res.data) {
        populateDataSet(res.data, res.pagination);
        if (value) {
          setSelectedRowItems(value);
        }
      }

      await fetchAllPermissions();

      setLoading(false);
    };
    fetchData();
  }, []);

  useEffect(() => {
    handleChange(selectedRowItems);
    setSelectName(selectedRowItems.map((row) => row.name).join(", "));
  }, [selectedRowItems, handleChange]);

  const [open, setOpen] = React.useState(false);

  const toggleModel = () => {
    setOpen(!open);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const toggleRow = (item: TRowItem) => {
    setSelectedRowItems((prevSelected) => {
      const isSelected = prevSelected.some(
        (selected) => selected.id === item.id,
      );
      if (isSelected) {
        return prevSelected.filter((i) => i.id !== item.id);
      } else {
        return [...prevSelected, item];
      }
    });
  };

  const toggleSelectAllRow = () => {
    const currentPageIds = dataset.map((item) => item.id);
    const currentPageSelectedCount = selectedRowItems.filter((item) =>
      currentPageIds.includes(item.id),
    ).length;

    if (currentPageSelectedCount === dataset.length) {
      if (selectedRowItems.length === allPermissions.length) {
        setSelectedRowItems([]);
      } else {
        setSelectedRowItems(allPermissions);
      }
    } else {
      setSelectedRowItems(allPermissions);
    }
  };

  const handleNextPage = async () => {
    const response = await paginateToNextPage(
      { is_deleted: false },
      populateDataSet,
    )();
    return response;
  };

  const handlePreviousPage = async () => {
    const response = await paginateToPreviousPage(
      { is_deleted: false },
      populateDataSet,
    )();
    return response;
  };

  const handlePageNumber = async (page: number) => {
    const response = await paginateToPageNumber(populateDataSet, {
      is_deleted: false,
    })(page);
    return response;
  };

  const getCurrentPageSelectedItems = () => {
    return selectedRowItems.filter((selected) =>
      dataset.some((current) => current.id === selected.id),
    );
  };

  const getSelectAllState = () => {
    const currentPageSelectedCount = getCurrentPageSelectedItems().length;
    const currentPageTotalCount = dataset.length;

    if (currentPageSelectedCount === 0) {
      return { checked: false, indeterminate: false };
    } else if (
      currentPageSelectedCount === currentPageTotalCount &&
      selectedRowItems.length === allPermissions.length
    ) {
      return { checked: true, indeterminate: false };
    } else {
      return { checked: false, indeterminate: true };
    }
  };

  return (
    <Box>
      <CustomInputButton
        loading={loading}
        label={label}
        placeholder={placeholder}
        value={selectName}
        handleChange={toggleModel}
        error={error}
        errorMessage={errorMessage}
        size={size}
      />
      <Dialog
        maxWidth={"xl"}
        open={open}
        sx={{ height: "100%" }}
        onClose={toggleModel}
      >
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          paddingRight={2}
        >
          <DialogTitle fontWeight={600}>{"User Permissions"}</DialogTitle>
          {selectedRowItems && selectedRowItems.length > 0 ? (
            <DialogActions>
              <ContainedButton onClick={handleClose} title="Continue" />
            </DialogActions>
          ) : (
            <Box marginTop={2}>
              <TextButton title="Continue" />
            </Box>
          )}
        </Stack>

        <DialogContent sx={styles.content}>
          <DamsTable
            isModel
            tableHeaders={columns}
            tableKeys={tkeys}
            data={dataset}
            hasPagination
            paginationState={pagination}
            onNextPage={handleNextPage}
            onPreviousPage={handlePreviousPage}
            setActivePage={handlePageNumber}
            useTableCheckbox
            onTableItemSelect={toggleRow}
            selectedRowItems={getCurrentPageSelectedItems()}
            toggleSelectAllRow={toggleSelectAllRow}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const styles = {
  filterBox: {
    width: "100%",
    marginBottom: 5,
  },
  content: (theme: Theme) => ({
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    [theme.breakpoints.up("xl")]: {
      width: "70vw",
    },
    width: "90vw",
  }),
  table: {
    width: "100vw",
    height: "70%",
  },
};

const tkeys = ["name"];
const columns = ["Permission Name"];
